import { useEffect, useMemo, useRef, useState } from 'react';
import { ControllerRenderProps, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import IntlTelInput, { IntlTelInputRef } from '@intl-tel-input/react';
import { getExample, parsePhoneNumber } from 'awesome-phonenumber';
import c from 'classnames';
import { down, up } from 'styled-breakpoints';
import styled, { css } from 'styled-components';
import ChevronIcon from 'src/assets/icons/phone-number-input-chevron.svg?url';
import { FlexColumn } from 'src/components/layout/flex-column';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useVisible } from 'src/hooks/use-visible';
import { BottomDrawer } from '../drawer/bottom/bottom-drawer';
import { inlineFormInputStyles, InputFooterActions } from './inline-text-inputs';

const mobileStyles = css`
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.koala};
  background: ${({ theme }) => theme.colors.white};

  &:focus {
    border-radius: 4px;
    border: 2px solid ${({ theme }) => theme.colors.gogo};
    background: ${({ theme }) => theme.colors.white};
    box-shadow: 0 3px 6px 0 rgba(165, 209, 223, 0.32);
  }
`;

// Sizes the read-only input to its content without `field-sizing` (unsupported in older
// Safari): a hidden copy of the displayed text sets the container width, and the input
// (`width: 0; min-width: 100%`, so it doesn't add its own intrinsic width) fills it.
const MobileDisplayInputContainer = styled.span<{ $active: boolean }>`
  display: inline-flex;
  flex-direction: column;
  max-width: 100%;

  ${({ $active }) =>
    $active &&
    css`
      border-radius: 4px;
      background-color: ${({ theme }) => theme.colors.alabaster};
    `}

  .iti {
    min-width: 100%;
    width: 0;
  }

  .iti__tel-input {
    background: transparent !important;
    border: none !important;
    padding-left: 30px !important;
    pointer-events: none !important;
    text-align: right !important;
  }
`;

const MobileDisplayInputSizer = styled.span`
  ${inlineFormInputStyles}
  border: none;
  height: 0;
  overflow: hidden;
  padding: 0 5px 0 30px;
  visibility: hidden;
  white-space: pre;
  width: auto;
`;

const StyledIntlTelInputWrapper = styled.span`
  font-family: ${({ theme }) => theme.font.regular};
  cursor: pointer;

  // intl-tel-input gives the flag button z-index: 1, the same as other popovers (e.g.
  // react-select menus), so the flag was painted over popovers opened from fields above.
  // Isolating the stacking context keeps that z-index local to the input. It's only
  // applied while not focused, so the country dropdown can still overlap other fields.
  &:not(:focus-within) {
    isolation: isolate;
  }

  .iti__tel-input {
    ${inlineFormInputStyles};

    ${down('md')} {
      border: 1px solid ${({ theme }) => theme.colors.koala};
    }
  }

  &:focus-within {
    .iti__tel-input:not(:focus) {
      background-color: ${({ theme }) => theme.colors.porcelain};
    }
  }

  .iti__country-container {
    .iti__flag {
      &.iti__globe {
        background-image: none;

        .iti__globe-svg {
          width: 16px;
          height: 16px;

          path {
            fill: ${({ theme }) => theme.colors.flint};
          }
        }
      }
    }

    .iti__search-input-wrapper {
      border-bottom: 1px solid ${({ theme }) => theme.colors.koala};

      .iti__search-icon {
        display: none;
      }

      input {
        color: ${({ theme }) => theme.colors.smalt};
        font-family: ${({ theme }) => theme.font.regular};
        font-size: 12px;
        line-height: 15px;
        padding: 10px;

        border: none;
        outline: none;
        box-shadow: none;
        border-radius: 4px;

        &::placeholder {
          color: ${({ theme }) => theme.colors.geyser};
        }
      }
    }

    .iti__selected-dial-code {
      display: none;
    }

    .iti__arrow {
      border: none;
      background-image: url('${ChevronIcon}');
      background-size: 10px 10px;
      background-repeat: no-repeat;
      width: 10px;
      height: 10px;
      transition: transform 0.2s ease;

      &.iti__arrow--up {
        transform: rotate(180deg);
      }
    }

    iti__arrow {
      border-width: 0px;
      display: inline-block;
      border-right: 2px solid #333;
      border-bottom: 2px solid #333;
      width: 12px;
      height: 12px;
      transition: transform 0.2s ease;
      transform: rotate(45deg);

      &.iti__arrow--up {
        transform: rotate(225deg);
      }
    }
  }

  .iti__country-selector {
    border-radius: 6px;
    border: 1px solid var(--Light-Greys-Koala, #e9eef2);
    background: #fff;

    /* shadow-popover */
    box-shadow: 0 20px 40px 0 rgba(91, 101, 112, 0.1);
    z-index: 9999;
    position: relative;
    background: white;
    border-radius: 6px !important;

    top: 3px !important;

    &:not(.iti__hide) {
      height: 218px !important;
      display: flex;
      flex-direction: column;
    }

    .iti__no-results {
      color: ${({ theme }) => theme.colors.flint};
      font-family: ${({ theme }) => theme.font.regular};
      font-size: 12px;
      line-height: 15px;
    }
  }

  .iti__country-list {
    background: white;
    border-radius: 6px !important;
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 5px;
    height: 100%;

    li {
      height: 32px;
      border-radius: 6px;
      color: ${({ theme }) => theme.colors.flint};
      font-family: ${({ theme }) => theme.font.regular};
      font-size: 12px;
      line-height: 15px;

      &:hover {
        background: ${({ theme }) => theme.colors.porcelain};
        color: ${({ theme }) => theme.colors.smalt};

        .iti__country-name {
          .iti__dial-code {
            color: ${({ theme }) => theme.colors.steel};
          }
        }
      }

      .iti__flag {
        width: 16px;
        margin-right: 10px;
      }

      .iti__country-name {
        .iti__dial-code {
          color: ${({ theme }) => theme.colors.flint};
        }
      }

      .iti__country-check {
        display: flex;

        svg {
          path {
            fill: ${({ theme }) => theme.colors.gogo};
          }
        }
      }
    }
  }

  &:focus-within {
    .iti__country-container {
      display: block;
    }

    .iti__tel-input {
      padding-left: 52px !important;
    }
  }
`;

const MobileInputContainer = styled(FlexColumn)`
  height: 100%;

  ${StyledIntlTelInputWrapper} {
    padding: 15px 15px 30px 15px;
  }

  .iti--inline-country-selector {
    .iti--input-container {
      padding: 10px;
    }

    .iti__country-selector {
      box-shadow: none;
      width: 100% !important;
      height: 100% !important;
      display: flex;
      flex-direction: column;

      .iti__country-list {
        padding: 15px;
        display: flex;
        flex-direction: column;
        gap: 8px;
        height: 100%;
        overflow: scroll;

        li.iti__country {
          min-height: 40px;
          border-radius: 4px;
          background: ${({ theme }) => theme.colors.porcelain};
          color: ${({ theme }) => theme.colors.flint};
          font-family: ${({ theme }) => theme.font.regular};
          font-size: 15px;
          line-height: 15px;

          &[aria-selected='true'] {
            color: ${({ theme }) => theme.colors.gogo};
            border-radius: 4px;
            background: ${({ theme }) => theme.colors.secondaryTopaz10};
          }

          .iti__country-check {
            margin-left: auto;
          }
        }
      }

      .iti__no-results {
        color: ${({ theme }) => theme.colors.flint};
        font-family: ${({ theme }) => theme.font.regular};
        font-size: 12px;
        line-height: 15px;
      }

      .iti__search-input-wrapper {
        padding: 10px 15px;
        border: none;
        border-bottom: 1px solid ${({ theme }) => theme.colors.koala};

        .iti__search-icon {
          left: 23px;
        }

        .iti__search-input {
          color: ${({ theme }) => theme.colors.smalt};
          font-family: ${({ theme }) => theme.font.regular};
          font-size: 15px;
          line-height: 16px;
          height: 38px;
          ${mobileStyles};

          &::placeholder {
            color: ${({ theme }) => theme.colors.geyser};
          }
        }

        .iti__search-clear {
          right: 19px;
        }

        input {
          border-radius: 4px;
          border: 1px solid ${({ theme }) => theme.colors.koala};
        }
      }
    }
  }
`;

const CountrySelectorContainer = styled(MobileInputContainer)`
  .iti--inline-country-selector {
    overflow: hidden;
  }
`;

type InternationalPhoneInputProps<T extends FieldValues> = {
  field: ControllerRenderProps<T>;
  testId?: string;
  error?: string;
};

export const InternationalPhoneInput = <T extends FieldValues>({
  field,
  testId,
  error,
}: InternationalPhoneInputProps<T>) => {
  const [isCountrySelectorOpen, setIsCountrySelectorOpen] = useState(false);
  const breakpointUpMd = useBreakpoint(up('md'));
  const visibilityHandler = useVisible();
  const [pivotValue, setPivotValue] = useState<string>(field.value ?? '');
  const { t } = useTranslation(['common']);
  const countrySelectorContainerRef = useRef<HTMLDivElement>(null);
  const [selectedCountry, setSelectedCountry] = useState('us');
  const [isFocused, setIsFocused] = useState(false);
  const drawerInputRef = useRef<IntlTelInputRef>(null);
  // IntlTelInput reformats/normalizes an initial `value` shortly after mount (once
  // its internal instance finishes loading), firing onChangeNumber even though the
  // user hasn't touched the field. Ignore that call until the input is actually
  // focused, so it doesn't look like a genuine edit to consumers (e.g. autosave).
  const hasInteractedRef = useRef(false);

  useEffect(() => {
    if (visibilityHandler.visible) {
      setPivotValue(field.value ?? '');
      drawerInputRef.current?.getInput()?.focus();
    }
    // Intentionally omitting field.value from deps: we only want to snapshot
    // the latest field value when the drawer *opens* (visible: false→true),
    // not on every external change while editing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibilityHandler.visible]);

  const commonIntlTelInputProps = useMemo(
    () => ({
      initialCountry: 'us' as const,
      numberDisplayFormat: 'NATIONAL' as const,
      separateDialCode: false,
      onOpenCountrySelector: () => {
        setIsCountrySelectorOpen(true);
      },
      onCloseCountrySelector: () => {
        setIsCountrySelectorOpen(false);
      },
      onChangeCountry: (iso2: string) => {
        setSelectedCountry(iso2);
      },
      formatAsYouType: true,
    }),
    [],
  );

  const exampleNumberPlaceholder = useMemo(() => {
    try {
      return getExample(selectedCountry.toUpperCase(), 'mobile').number?.national;
    } catch {
      return t('common:inputPlaceholder.phone');
    }
  }, [selectedCountry, t]);

  // Mirrors what the read-only mobile input shows (IntlTelInput renders the value in
  // national format) so MobileDisplayInputSizer can size it.
  const mobileDisplayValue = useMemo(() => {
    if (!field.value) {
      return t('common:inputPlaceholder.phone');
    }

    return parsePhoneNumber(field.value).number?.national ?? field.value;
  }, [field.value, t]);

  const commonInputProps = useMemo(
    () => ({
      placeholder: isFocused
        ? exampleNumberPlaceholder
        : t('common:inputPlaceholder.phone'),
      className: c({
        'input-error': Boolean(error),
      }),
      onFocus: () => {
        hasInteractedRef.current = true;
        setIsFocused(true);
      },
      onBlur: () => setIsFocused(false),
    }),
    [isFocused, exampleNumberPlaceholder, t, error],
  );

  return (
    <StyledIntlTelInputWrapper>
      {breakpointUpMd ? (
        <IntlTelInput
          loadUtils={() => import('intl-tel-input/utils')}
          {...commonIntlTelInputProps}
          onChangeNumber={(value) => {
            if (!hasInteractedRef.current) {
              return;
            }

            field.onChange(value);
          }}
          value={field.value}
          inputProps={{
            ...commonInputProps,
            ...(testId ? { 'data-lgg-id': testId } : {}),
          }}
        />
      ) : (
        <div>
          <MobileDisplayInputContainer
            onClick={visibilityHandler.show}
            $active={visibilityHandler.visible}
          >
            <IntlTelInput
              loadUtils={() => import('intl-tel-input/utils')}
              {...commonIntlTelInputProps}
              disabled={true}
              value={field.value}
              countrySelectorMode="OFF"
              inputProps={{
                ...commonInputProps,
              }}
            />
            <MobileDisplayInputSizer aria-hidden>
              {mobileDisplayValue}
            </MobileDisplayInputSizer>
          </MobileDisplayInputContainer>
          <BottomDrawer
            title={t('common:phoneNumber')}
            onClose={visibilityHandler.close}
            visible={visibilityHandler.visible}
            push={false}
            footer={
              <InputFooterActions
                onSave={() => {
                  field.onChange(pivotValue);
                  visibilityHandler.close();
                }}
                onCancel={visibilityHandler.close}
              />
            }
            fullHeight={false}
          >
            <MobileInputContainer>
              <StyledIntlTelInputWrapper style={{ padding: '15px 15px 30px 15px' }}>
                <IntlTelInput
                  ref={drawerInputRef}
                  loadUtils={() => import('intl-tel-input/utils')}
                  {...commonIntlTelInputProps}
                  onChangeNumber={(number) => {
                    setPivotValue(number);
                  }}
                  value={pivotValue}
                  countrySelectorMode="DROPDOWN"
                  inputProps={commonInputProps}
                  dropdownParent={countrySelectorContainerRef.current}
                />
              </StyledIntlTelInputWrapper>
            </MobileInputContainer>
          </BottomDrawer>
          <BottomDrawer
            title={t('common:selectCountry')}
            onClose={() => setIsCountrySelectorOpen(false)}
            visible={isCountrySelectorOpen}
            destroyOnClose={false}
            forceRender
            zIndex={1001}
            push={false}
            footer={<></>}
            fullHeight
          >
            {/* IntlTelInput portals its country-selector dropdown into this node via
                `dropdownParent` (read once on mount), so it must already exist in the DOM
                before the input below mounts - forceRender guarantees that. */}
            <CountrySelectorContainer ref={countrySelectorContainerRef} />
          </BottomDrawer>
        </div>
      )}
    </StyledIntlTelInputWrapper>
  );
};
